import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// Production domain - canonical URLs should always point here
const PRODUCTION_DOMAIN = 'https://thegdevelopments.co';

// Fallback only - each page MUST provide its own title
const FALLBACK_DESCRIPTION = 'G Developments is a leading Egyptian real estate developer delivering premium residential, commercial, and coastal projects across Egypt.';

/**
 * SEO Component - Per-page head rendering
 * Each page defines its own <title> and meta tags
 * NO global defaults - titles are page-specific
 * 
 * Title Logic:
 * - Homepage: "G Developments | Official Website"
 * - Project pages: "{Project Name} | G Developments"
 * - Article pages: "{Article Title} | G Developments"
 * 
 * @param {string} title - REQUIRED: Page title for SEO (must be unique per page)
 * @param {string} description - Page meta description
 * @param {string} canonicalPath - Optional custom canonical path
 * @param {string} keywords - Optional keywords
 */
const SEO = ({ title, description, canonicalPath, keywords }) => {
  const location = useLocation();
  
  // Title is REQUIRED - no fallback to generic title
  // Reject any Emergent/framework titles
  const pageTitle = title && 
    !title.toLowerCase().includes('emergent') && 
    !title.toLowerCase().includes('fullstack') 
      ? title 
      : 'G Developments | Official Website';
    
  const metaDescription = description || FALLBACK_DESCRIPTION;
  const metaKeywords = keywords || 'G Developments, Egyptian real estate, luxury properties Egypt';
  
  // Canonical URL from production domain
  const path = canonicalPath || location.pathname;
  const canonicalUrl = `${PRODUCTION_DOMAIN}${path}`;
  
  // Directly update document head on mount/change
  useEffect(() => {
    // Set document title immediately
    document.title = pageTitle;
    
    // Update or create canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = canonicalUrl;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = canonicalUrl;
      document.head.appendChild(canonical);
    }
    
    // Update meta title tag
    let metaTitle = document.querySelector('meta[name="title"]');
    if (metaTitle) {
      metaTitle.content = pageTitle;
    }
    
    // Update OG tags
    const ogTags = {
      'meta[property="og:url"]': canonicalUrl,
      'meta[property="og:title"]': pageTitle,
      'meta[property="og:description"]': metaDescription
    };
    
    Object.entries(ogTags).forEach(([selector, value]) => {
      const el = document.querySelector(selector);
      if (el) el.content = value;
    });
    
  }, [pageTitle, canonicalUrl, metaDescription]);
  
  return (
    <Helmet>
      {/* Page-specific title - NOT overridden by layout */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content="index, follow" />
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content="G Developments" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
    </Helmet>
  );
};

export default SEO;
